(() => {
  'use strict';

  const SELECTOR = '[data-pencil-banner]';

  class PencilBanner {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-pencil-slide]'));
      this.previousButton = root.querySelector('[data-pencil-prev]');
      this.nextButton = root.querySelector('[data-pencil-next]');
      this.playPauseButton = root.querySelector('[data-pencil-play-pause]');
      this.status = root.querySelector('[data-pencil-status]');
      this.mode = root.dataset.mode || 'manual';
      this.intervalDuration = Math.max(Number(root.dataset.interval || 5000), 1000);
      this.index = 0;
      this.autoplayTimer = null;
      this.countdownTimer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused =
        this.mode !== 'autoplay' ||
        root.dataset.startPaused === 'true' ||
        this.reducedMotion.matches;

      this.boundVisibilityChange = this.handleVisibilityChange.bind(this);
      this.boundReducedMotionChange = this.handleReducedMotionChange.bind(this);

      if (!this.slides.length) return;

      this.bindEvents();
      this.show(0, 'initial');
      this.startCountdowns();
      this.syncAutoplay();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => {
        this.go(this.index - 1, 'previous');
      });

      this.nextButton?.addEventListener('click', () => {
        this.go(this.index + 1, 'next');
      });

      this.playPauseButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePlayPauseButton();
        this.syncAutoplay();
        this.emitAnalytics(this.paused ? 'pause' : 'play');
      });

      this.root.addEventListener('keydown', (event) => {
        if (
          this.slides.length <= 1 ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey
        ) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.index - 1, 'keyboard_previous');
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + 1, 'keyboard_next');
        }
      });

      this.root.addEventListener('click', (event) => {
        const link = event.target.closest?.(
          '[data-pencil-link], .pencil-banner__message a'
        );
        if (!link) return;

        this.emitAnalytics('click', {
          linkUrl: link.href,
          linkText: link.textContent.trim()
        });
      });

      document.addEventListener('visibilitychange', this.boundVisibilityChange);

      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.boundReducedMotionChange);
      } else if (this.reducedMotion.addListener) {
        this.reducedMotion.addListener(this.boundReducedMotionChange);
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.syncAutoplay();
      }
    }

    handleReducedMotionChange(event) {
      if (event.matches) this.paused = true;
      this.updatePlayPauseButton();
      this.syncAutoplay();
    }

    go(targetIndex, source = 'programmatic') {
      if (this.slides.length <= 1) return;

      const wrappedIndex =
        (targetIndex + this.slides.length) % this.slides.length;

      this.show(wrappedIndex, source);

      if (this.mode === 'autoplay' && !this.paused) {
        this.syncAutoplay();
      }
    }

    show(index, source = 'programmatic') {
      this.index = index;
      this.root.dataset.activeIndex = String(index);

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.hidden = !isActive;
        slide.setAttribute('aria-hidden', String(!isActive));

        if ('inert' in slide) {
          slide.inert = !isActive;
        }
      });

      this.updatePlayPauseButton();
      this.updateStatus();
      this.emitAnalytics('impression', { source });
    }

    updateStatus() {
      if (!this.status || this.slides.length <= 1) return;
      this.status.textContent = `Promotion ${this.index + 1} of ${this.slides.length}`;
    }

    updatePlayPauseButton() {
      if (!this.playPauseButton) return;

      this.playPauseButton.setAttribute('aria-pressed', String(this.paused));
      this.playPauseButton.setAttribute(
        'aria-label',
        this.paused ? 'Play promotional banner' : 'Pause promotional banner'
      );

      this.playPauseButton.innerHTML = this.paused
        ? '<span class="pencil-banner__play-icon" aria-hidden="true"></span>'
        : '<span class="pencil-banner__pause-icon" aria-hidden="true"><i></i><i></i></span>';
    }

    syncAutoplay() {
      this.stopAutoplay();

      if (
        this.mode !== 'autoplay' ||
        this.paused ||
        this.reducedMotion.matches ||
        this.slides.length <= 1 ||
        document.hidden
      ) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.go(this.index + 1, 'autoplay');
      }, this.intervalDuration);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
      }
      this.autoplayTimer = null;
    }

    startCountdowns() {
      const countdowns = Array.from(
        this.root.querySelectorAll('[data-pencil-countdown]')
      );

      if (!countdowns.length) return;

      countdowns.forEach((countdown) => {
        const parsedEnd = this.parseCountdownEnd(countdown);

        if (!Number.isFinite(parsedEnd)) {
          countdown.dataset.invalid = 'true';
          console.warn(
            'Pencil banner countdown requires a valid MM/DD/YYYY date and Pacific Time selection.',
            {
              date: countdown.dataset.endDate,
              time: countdown.dataset.endTime,
              timeZone: countdown.dataset.timeZone
            }
          );
          return;
        }

        countdown.dataset.endTimestamp = String(parsedEnd);
      });

      const tick = () => {
        countdowns.forEach((countdown) => this.updateCountdown(countdown));
      };

      tick();
      this.countdownTimer = window.setInterval(tick, 1000);
    }


    parseCountdownEnd(countdown) {
      const dateParts = this.parseDateInput(countdown.dataset.endDate || '');
      const timeParts = this.parseTimeInput(countdown.dataset.endTime || '');
      const timeZone = countdown.dataset.timeZone || 'America/Los_Angeles';

      if (!dateParts || !timeParts) return Number.NaN;

      return this.zonedDateTimeToTimestamp(
        {
          ...dateParts,
          ...timeParts,
          second: 0
        },
        timeZone
      );
    }

    parseDateInput(value) {
      const normalized = value.trim();
      const usMatch = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      const isoMatch = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

      const year = Number(usMatch?.[3] || isoMatch?.[1]);
      const month = Number(usMatch?.[1] || isoMatch?.[2]);
      const day = Number(usMatch?.[2] || isoMatch?.[3]);

      if (!year || !month || !day) return null;

      const testDate = new Date(Date.UTC(year, month - 1, day));
      const isValid =
        testDate.getUTCFullYear() === year &&
        testDate.getUTCMonth() === month - 1 &&
        testDate.getUTCDate() === day;

      return isValid ? { year, month, day } : null;
    }

    parseTimeInput(value) {
      const match = value.trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
      if (!match) return null;

      return {
        hour: Number(match[1]),
        minute: Number(match[2])
      };
    }

    zonedDateTimeToTimestamp(dateTime, timeZone) {
      const targetAsUtc = Date.UTC(
        dateTime.year,
        dateTime.month - 1,
        dateTime.day,
        dateTime.hour,
        dateTime.minute,
        dateTime.second || 0
      );

      let timestamp = targetAsUtc;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        const offset = this.getTimeZoneOffset(timestamp, timeZone);
        if (!Number.isFinite(offset)) return Number.NaN;

        const nextTimestamp = targetAsUtc - offset;
        if (Math.abs(nextTimestamp - timestamp) < 1000) {
          timestamp = nextTimestamp;
          break;
        }

        timestamp = nextTimestamp;
      }

      const resolved = this.getZonedDateTimeParts(timestamp, timeZone);
      const matchesRequestedTime =
        resolved &&
        resolved.year === dateTime.year &&
        resolved.month === dateTime.month &&
        resolved.day === dateTime.day &&
        resolved.hour === dateTime.hour &&
        resolved.minute === dateTime.minute;

      return matchesRequestedTime ? timestamp : Number.NaN;
    }

    getTimeZoneOffset(timestamp, timeZone) {
      const parts = this.getZonedDateTimeParts(timestamp, timeZone);
      if (!parts) return Number.NaN;

      const zonedAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
      );

      return zonedAsUtc - Math.floor(timestamp / 1000) * 1000;
    }

    getZonedDateTimeParts(timestamp, timeZone) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        });

        const values = {};
        formatter.formatToParts(new Date(timestamp)).forEach((part) => {
          if (part.type !== 'literal') values[part.type] = Number(part.value);
        });

        if (
          !Number.isFinite(values.year) ||
          !Number.isFinite(values.month) ||
          !Number.isFinite(values.day) ||
          !Number.isFinite(values.hour) ||
          !Number.isFinite(values.minute) ||
          !Number.isFinite(values.second)
        ) {
          return null;
        }

        return values;
      } catch (error) {
        console.warn('Unable to resolve the pencil banner Pacific Time countdown.', error);
        return null;
      }
    }

    updateCountdown(countdown) {
      const endTimestamp = Number(countdown.dataset.endTimestamp);
      if (!Number.isFinite(endTimestamp)) return;

      const area = countdown.closest('.pencil-banner__countdown-area');
      const endsIn = area?.querySelector('[data-countdown-ends-in]');
      const expired = area?.querySelector('[data-countdown-expired]');
      const remainingMilliseconds = endTimestamp - Date.now();

      if (remainingMilliseconds <= 0) {
        countdown.hidden = true;
        if (endsIn) endsIn.hidden = true;
        if (expired) expired.hidden = false;
        countdown.dataset.state = 'expired';
        countdown.setAttribute('aria-label', 'Offer ended');
        return;
      }

      countdown.hidden = false;
      if (endsIn) endsIn.hidden = false;
      if (expired) expired.hidden = true;

      const totalSeconds = Math.floor(remainingMilliseconds / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.setCountdownUnit(countdown, 'days', days);
      this.setCountdownUnit(countdown, 'hours', hours);
      this.setCountdownUnit(countdown, 'minutes', minutes);
      this.setCountdownUnit(countdown, 'seconds', seconds);

      const daysGroup = countdown.querySelector(
        '[data-countdown-group="days"]'
      );

      if (daysGroup) {
        daysGroup.hidden = days === 0;
      }

      countdown.dataset.state =
        remainingMilliseconds < 60000 ? 'final-minute' : 'active';

      const spokenDays = days > 0 ? `${days} days, ` : '';
      countdown.setAttribute(
        'aria-label',
        `Offer ends in ${spokenDays}${hours} hours, ${minutes} minutes, and ${seconds} seconds`
      );
    }

    setCountdownUnit(countdown, unit, value) {
      const target = countdown.querySelector(
        `[data-countdown-unit="${unit}"]`
      );

      if (target) {
        target.textContent = String(value).padStart(2, '0');
      }
    }

    emitAnalytics(action, extra = {}) {
      const activeSlide = this.slides[this.index];
      const detail = {
        event: `pencil_banner_${action}`,
        component: 'pencil_banner',
        action,
        sectionId: this.root.dataset.sectionId || null,
        promotionId: activeSlide?.dataset.promotionId || null,
        blockId: activeSlide?.dataset.blockId || null,
        mode: this.mode,
        position: this.index + 1,
        totalPromotions: this.slides.length,
        ...extra
      };

      this.root.dispatchEvent(
        new CustomEvent('pencil-banner:analytics', {
          bubbles: true,
          detail
        })
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(detail);
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex(
        (slide) => slide.dataset.blockId === blockId
      );

      if (index === -1) return;

      if (this.mode === 'autoplay') {
        this.paused = true;
        this.updatePlayPauseButton();
        this.syncAutoplay();
      }

      this.show(index, 'theme_editor');
    }

    destroy() {
      this.stopAutoplay();

      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
      }

      document.removeEventListener(
        'visibilitychange',
        this.boundVisibilityChange
      );

      if (this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener(
          'change',
          this.boundReducedMotionChange
        );
      } else if (this.reducedMotion.removeListener) {
        this.reducedMotion.removeListener(
          this.boundReducedMotionChange
        );
      }

      this.root.__pencilBanner = null;
    }
  }

  function initialize(root = document) {
    root.querySelectorAll(SELECTOR).forEach((banner) => {
      if (!banner.__pencilBanner) {
        banner.__pencilBanner = new PencilBanner(banner);
      }
    });
  }

  function initializeSection(sectionElement) {
    if (!sectionElement) return;

    if (sectionElement.matches?.(SELECTOR)) {
      if (!sectionElement.__pencilBanner) {
        sectionElement.__pencilBanner = new PencilBanner(sectionElement);
      }
      return;
    }

    initialize(sectionElement);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeSection(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(SELECTOR).forEach((banner) => {
      banner.__pencilBanner?.destroy();
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest?.('[data-pencil-slide]') || event.target;
    const banner = block.closest?.(SELECTOR);
    const blockId = event.detail?.blockId || block.dataset?.blockId;

    banner?.__pencilBanner?.selectBlock(blockId);
  });
})();
